import { ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import { ActionPlanFeatureService } from '@/features/action-plan/action-plan-feature.service';
import { BuildingVisualizationFeatureService } from '@/features/building-visualization/building-visualization-feature.service';
import { BusinessIndicatorsFeatureService } from '@/features/business-indicators/business-indicators.service';
import { CurrentUserFeatureService } from '@/features/current-user/current-user-feature.service';
import { IpmvpFeatureService } from '@/features/ipmvp/ipmvp-feature.service';
import { ReportsFeatureService } from '@/features/reports/reports-feature.service';
import { Page, RoutingService } from '@/services/routing/routing.service';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { IconName } from '@/ui/icon/ui-icon';
import type { UiMenuItem } from '@/ui/menu/ui-menu';
import { PlainDatePipe } from '@/utils/temporal/plain-date/plain-date.pipe';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Overlay, OverlayConfig, type OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { Component, type ElementRef, Input, ViewChild, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { type Observable, firstValueFrom, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccessLevel } from '../../../../interfaces/Access.interface';
import { AdvizeoUiElementSize } from '../../../../interfaces/AdvizeoUi.interface';
import type { Period } from '../../../../interfaces/Building.interface';
import type { ClassificationNodeId } from '../../../../interfaces/Classification.interface';
import type { Project } from '../../../../interfaces/Project.interface';
import type { UserCore } from '../../../../interfaces/User.interface';
import { RosaAuthService } from '../../../../libs/rosa';
import { ClassificationNodeNamePipe } from '../../../../pipes/classification-node-name.pipe';
import { TranslatePeriodPipe } from '../../../../pipes/translate-period.pipe';
import { AuthProvider } from '../../../../providers';
import { DashboardingProvider } from '../../../../providers/dashboarding.provider';
import { ProfileProvider } from '../../../../providers/profile/profile.provider';
import { ProjectsProvider } from '../../../../providers/projects/projects.provider';
import {
    ACCESS_PROJECT_ADMINISTRATION,
    CONNECTED_AS_RIGHT_ACCESS,
    CREATE_BUILDING_RIGHT_ACCESS,
    CREATE_PROJECT_RIGHT_ACCESS,
    SUPPLIERS_PORTAL_ACCESS
} from '../../../../providers/rights.constants';
import { AdvizeoPopoverService } from '../../../../services/advizeo-popover.service';
import { ProjectSelectComponent } from '../../../project-select/project-select';
import { MainClassificationNodeSelectorComponent } from '../main-classification-node-selector/main-classification-node-selector.component';

@Component({
    selector: 'main-header',
    templateUrl: './main-header.component.html',
    styleUrls: ['./main-header.component.scss'],
    imports: [
        UiIconButtonComponent,
        ProjectSelectComponent,
        AsyncPipe,
        UpperCasePipe,
        TranslatePipe,
        TranslatePeriodPipe,
        ClassificationNodeNamePipe,
        PlainDatePipe
    ]
})
export class MainHeaderComponent {
    private _profileProvider = inject(ProfileProvider);
    private _projectsProvider = inject(ProjectsProvider);
    private _translate = inject(TranslateService);
    private _advizeoPopoverService = inject(AdvizeoPopoverService);
    private _routingService = inject(RoutingService);
    private _authProvider = inject(AuthProvider);
    private _rosaAuthService = inject(RosaAuthService);
    private _overlay = inject(Overlay);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _reportsFeatureService = inject(ReportsFeatureService);
    private _businessIndicatorsFeatureService = inject(BusinessIndicatorsFeatureService);
    private _currentUserFeatureService = inject(CurrentUserFeatureService);
    private _actionPlanFeatureService = inject(ActionPlanFeatureService);
    private _buildingVisualizationFeatureService = inject(BuildingVisualizationFeatureService);
    private _ipmvpFeatureService = inject(IpmvpFeatureService);
    private _applicationModulesProvider = inject(ApplicationModulesService);

    isAuthenticated$!: Observable<boolean>;
    currentUser$!: Observable<UserCore>;
    project$!: Observable<Project | null>;
    profilePicture$!: Observable<string>;
    currentProjectLogoUrl$!: Observable<string | null>;
    classificationNodeId$!: Observable<ClassificationNodeId | null>;

    read: AccessLevel = AccessLevel.READ;

    @ViewChild('selectorAnchor', { static: false }) selectorAnchor!: ElementRef<any>;
    @Input() simplifiedHeader: boolean = false;

    reportType$!: Observable<string>;
    selectedPeriod$!: Observable<Period | null>;
    currentDate = Temporal.Now.plainDateISO();

    ACCESS_PROJECT_ADMINISTRATION = ACCESS_PROJECT_ADMINISTRATION;

    private _overlayRef: OverlayRef | null = null;

    isClassificationNodeSelectorOpen: boolean = false;

    ngOnInit() {
        this.isAuthenticated$ = this._rosaAuthService.isAuthenticated$();
        this.currentUser$ = this._profileProvider.getUser$();
        this.profilePicture$ = this._profileProvider.getUserProfilePictureUrl$();
        this.project$ = this._projectsProvider.getUserCurrentProject$();
        this.currentProjectLogoUrl$ = this.project$.pipe(map(project => (project ? project.logoUrl : null)));

        this.classificationNodeId$ = this._dashboardingProvider.getNullableSelectedNodeId$();

        this.selectedPeriod$ = this._dashboardingProvider.getSelectedPeriod$();
        this.reportType$ = this._reportsFeatureService.getCurrentlyPrintingReportType$();
    }

    async openMoreMenu(myEvent: MouseEvent) {
        const isMonoBuilding = await firstValueFrom(this._dashboardingProvider.getIsMonoBuilding$());

        const menuItems: UiMenuItem[] = [];

        if (this.canAccessFeature(CREATE_BUILDING_RIGHT_ACCESS)) {
            menuItems.push({
                label: this._translate.instant('BUTTONS.ADD_BUILDING'),
                icon: IconName.BUILDING,
                click: () => {
                    this._buildingVisualizationFeatureService.goToBuildingCreation();
                }
            });
        }

        if (
            !this._applicationModulesProvider.hasModuleAccessR(ApplicationModuleCode.HIDE_TO_GERMAN) &&
            !this._applicationModulesProvider.hasModuleAccessR(ApplicationModuleCode.HIDE_TO_ITALY)
        ) {
            menuItems.push({
                label: this._translate.instant('POPOVERS.HOME_BUILDING.ADD_A_SYSTEM'),
                icon: IconName.SYSTEM,
                click: () => {
                    this._routingService.goTo(Page.SYSTEM_ADD, {});
                }
            });
        }

        menuItems.push(
            {
                label: this._translate.instant('POPOVERS.BILLS.ADD_DELIVERY_POINT'),
                icon: IconName.DELIVERY_POINT,
                click: () => {
                    this._routingService.goTo(Page.DELIVERY_POINT_ADD, {});
                }
            },
            {
                label: this._translate.instant('BUTTONS.ADD_ACTION'),
                icon: IconName.ACTION,
                click: () => {
                    this._actionPlanFeatureService.requestActionCreation();
                }
            }
        );

        if (
            this.canAccessFeature(SUPPLIERS_PORTAL_ACCESS) &&
            !this._applicationModulesProvider.hasModuleAccessR(ApplicationModuleCode.HIDE_TO_GERMAN) &&
            !this._applicationModulesProvider.hasModuleAccessR(ApplicationModuleCode.HIDE_TO_ITALY)
        ) {
            menuItems.push({
                label: this._translate.instant('POPOVERS.BILLS.ADD_BILLS'),
                icon: IconName.SUPPLIER,
                click: () => {
                    this._routingService.goTo(Page.SUPPLIER_PORTAL_ACCESS, {});
                }
            });
        }

        if (isMonoBuilding) {
            const buildingId = await firstValueFrom(this._dashboardingProvider.getMeaningfulBuildingId$());
            menuItems.push({
                label: this._translate.instant('POPOVERS.INDEX_READING.TITLE'),
                icon: IconName.MANUAL_READINGS,
                click: () => {
                    this._routingService.goTo(Page.METERS_LIST, { buildingUid: buildingId });
                }
            });
        }

        // TO REMOVED (BillsImportPage to edit/split/refactor)
        menuItems.push({
            label: this._translate.instant('POPOVERS.BILLS.IMPORT_BILL'),
            icon: IconName.INVOICE,
            click: () => {
                this._routingService.goTo(Page.BILLS_IMPORT_FILES, {});
            }
        });

        if (this.canAccessFeature(CREATE_PROJECT_RIGHT_ACCESS)) {
            menuItems.push({
                label: this._translate.instant('POPOVERS.PROJECT_CREATION.TITLE'),
                icon: IconName.PROJECT,
                click: () => {
                    this._projectsProvider.requestProjectCreation();
                }
            });
        }

        this._advizeoPopoverService.openMenu(
            {
                title: this._translate.instant('MENU.TITLE.CREATE'),
                titleSize: AdvizeoUiElementSize.LARGE,
                items: menuItems
            },
            myEvent.target! as HTMLElement
        );
    }

    openUserMenu(myEvent: Event) {
        const buttons: UiMenuItem[] = [
            {
                label: this._translate.instant('MENU.PROFILE'),
                icon: IconName.PROFILE,
                click: () => {
                    this._currentUserFeatureService.goToProfile();
                }
            },
            {
                label: this._translate.instant('MENU.SETTINGS'),
                icon: IconName.CONFIGURE,
                click: () => {
                    this._currentUserFeatureService.goToSettings();
                }
            }
        ];

        if (this._profileProvider.hasFeatureAccess(CONNECTED_AS_RIGHT_ACCESS) && !this._rosaAuthService.isConnectedAs()) {
            buttons.push({
                label: this._translate.instant('MENU.CONNECTED_AS'),
                icon: IconName.LOG_AS,
                click: () => {
                    this._routingService.resetNavigationTo(Page.CONNECTED_AS, {});
                }
            });
        } else if (this._rosaAuthService.isConnectedAs()) {
            buttons.push({
                label: this._translate.instant('MENU.BACK_TO_MAIN_ACCOUNT'),
                icon: IconName.LOG_AS,
                click: () => {
                    this._authProvider.backToMainAccountAndRedirectToMainPage();
                }
            });
        }

        buttons.push({
            label: this._translate.instant('MENU.DISCONNECT'),
            icon: IconName.LOG_OUT,
            click: () => {
                this._authProvider.logout();
            }
        });

        this._advizeoPopoverService.openMenu(
            {
                title: this._profileProvider.getUserName(),
                titleSize: AdvizeoUiElementSize.LARGE,
                items: buttons
            },
            myEvent.target! as HTMLElement
        );
    }

    openAdminMenu(myEvent: MouseEvent) {
        const buttons: UiMenuItem[] = [
            {
                label: this._translate.instant('MENU.USERS_PROFILES'),
                icon: IconName.USERS,
                click: () => {
                    this._routingService.resetNavigationTo(Page.USERS_PROFILES_MANAGEMENT, {});
                }
            },
            {
                label: this._translate.instant('MENU.CLASSIFICATION'),
                icon: IconName.CLASSIFICATION,
                click: () => {
                    this._routingService.resetNavigationTo(Page.CLASSIFICATIONS_MANAGEMENT, {});
                }
            },
            {
                label: this._translate.instant('MENU.BUSINESS_INDICATORS'),
                icon: IconName.INDICATORS_AND_OBJECTIVES,
                click: () => {
                    this._businessIndicatorsFeatureService.goToAdminFeature();
                }
            },
            {
                label: this._translate.instant('MENU.ATTRIBUTE_MANAGEMENT'),
                icon: IconName.ATTRIBUTE,
                click: () => {
                    this._routingService.resetNavigationTo(Page.ATTRIBUTES_MANAGEMENT, {});
                }
            },
            ...(this._ipmvpFeatureService.hasFeatureAccessR()
                ? [
                      {
                          label: this._translate.instant('MENU.IPMVP'),
                          icon: IconName.IPMVP,
                          click: () => {
                              this._ipmvpFeatureService.goToFeature();
                          }
                      }
                  ]
                : []),
            {
                label: this._translate.instant('MENU.IMPORT_HISTORY'),
                icon: IconName.HISTORY,
                click: () => {
                    this._routingService.resetNavigationTo(Page.IMPORT_HISTORY, {});
                }
            },
            {
                label: this._translate.instant('MENU.SETTINGS'),
                icon: IconName.CONFIGURE,
                click: () => {
                    this._routingService.resetNavigationTo(Page.PROJECT_SETTINGS, {});
                }
            }
        ];

        this._advizeoPopoverService.openMenu(
            {
                title: this._translate.instant('MENU.ADMIN'),
                titleSize: AdvizeoUiElementSize.LARGE,
                items: buttons
            },
            myEvent.target! as HTMLElement
        );
    }

    onClassificationNodeIdChange(classificationNodeId: ClassificationNodeId) {
        if (isNotNullish(classificationNodeId)) {
            this._dashboardingProvider.setSelectedNodeId(classificationNodeId);
        }
    }

    canAccessFeature(code: string): boolean {
        return this._profileProvider.hasFeatureAccess(code);
    }

    openNodeSelector() {
        const positionStrategy = this._overlay
            .position()
            .flexibleConnectedTo(this.selectorAnchor.nativeElement)
            .withPositions([{ overlayX: 'start', overlayY: 'top', originX: 'start', originY: 'bottom' }]);

        const overlayConfig = new OverlayConfig({
            positionStrategy,
            width: `${this.selectorAnchor.nativeElement.getBoundingClientRect().width}px`,
            height: '450px',
            hasBackdrop: true,
            backdropClass: 'invisible-backdrop',
            panelClass: 'node-selector-panel'
        });

        this._overlayRef = this._overlay.create(overlayConfig);

        const component = this._overlayRef.attach(new ComponentPortal(MainClassificationNodeSelectorComponent, null));
        this.isClassificationNodeSelectorOpen = true;

        firstValueFrom(merge(this._overlayRef.backdropClick(), component.instance.close)).then(() => {
            if (this._overlayRef) {
                this._overlayRef.dispose();
            }
            this.isClassificationNodeSelectorOpen = false;
        });
    }
}
